import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminChangePassword = ({ onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = onBack || (() => navigate(-1));
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!currentPassword) {
      toast.error('Enter current password');
      return;
    }
    if (!newPassword || newPassword.length < 5) {
      toast.error('New password must be at least 5 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp-reset-password`, null, {
        params: {
          phone: user.phone,
          current_password: currentPassword,
          new_password: newPassword
        }
      });
      toast.success('Password updated! Please login again.');
      setTimeout(() => window.location.href = '/admin-login', 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
        <button onClick={goBack} className="p-1" data-testid="admin-change-password-back"><ArrowLeft size={24} /></button>
        <h1 className="text-lg font-semibold">Change Password</h1>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full px-3 py-2 border rounded-lg pr-10 outline-none focus:border-[#075E54]"
                data-testid="current-password-input"
              />
              <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-2.5 text-gray-500">
                {showCurrent ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 5 chars)"
                className="w-full px-3 py-2 border rounded-lg pr-10 outline-none focus:border-[#075E54]"
                data-testid="new-password-input"
              />
              <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-2.5 text-gray-500">
                {showNew ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-[#075E54]"
              data-testid="confirm-password-input"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !currentPassword || !newPassword}
            className="w-full py-3 bg-[#075E54] text-white rounded-lg font-semibold hover:bg-[#064E46] disabled:opacity-50"
            data-testid="change-password-btn"
          >
            {loading ? 'Updating...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminChangePassword;
